import React from 'react';
import { Space, Typography } from 'antd';
import { FolderOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as Icons from '@ant-design/icons';

const { Text } = Typography;

type AntIconComponent = React.ComponentType<React.ComponentProps<typeof FolderOutlined>>;

export interface EquipmentCategoryDisplayProps {
  categoryId?: string;
  category?: {
    id: string;
    categoryLabel?: string | null;
    icon?: string | null;
    color?: string | null;
  };
  showIcon?: boolean;
  noLink?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: React.CSSProperties;
}

/**
 * 設備カテゴリー情報を統一的に表示するコンポーネント
 *
 * @example
 * // 基本的な使用（リンク付き、設備検索にカテゴリーフィルタを適用）
 * <EquipmentCategoryDisplay category={category} />
 *
 * @example
 * // アイコンなし
 * <EquipmentCategoryDisplay category={category} showIcon={false} />
 *
 * @example
 * // リンクなし
 * <EquipmentCategoryDisplay category={category} noLink />
 */
export function EquipmentCategoryDisplay({
  categoryId,
  category,
  showIcon = true,
  noLink = false,
  size = 'default',
  style
}: EquipmentCategoryDisplayProps): JSX.Element | null {
  const navigate = useNavigate();

  // categoryオブジェクトまたはcategoryIdが必要
  if (!category && !categoryId) {
    return null;
  }

  const effectiveCategory = category || {
    id: categoryId!,
    categoryLabel: null,
    icon: null,
    color: null
  };

  const displayName =
    effectiveCategory.categoryLabel ||
    effectiveCategory.id;

  const handleClick = () => {
    if (!noLink && effectiveCategory.id) {
      // 設備検索ページにカテゴリーフィルタを適用してナビゲート
      navigate(`/app/equipments?categoryId=${effectiveCategory.id}`);
    }
  };

  // アイコンコンポーネントの取得
  const IconComponent = effectiveCategory.icon
    ? ((Icons as unknown as Record<string, AntIconComponent>)[effectiveCategory.icon] ||
        FolderOutlined)
    : FolderOutlined;

  const iconSize = size === 'small' ? 14 : size === 'large' ? 20 : 16;
  const fontSize = size === 'small' ? 12 : size === 'large' ? 16 : 14;

  // カテゴリーカラーの適用
  const iconColor = effectiveCategory.color || '#52c41a';

  return (
    <Space size={8} style={{ cursor: noLink ? 'default' : 'pointer', ...style }}>
      {showIcon && <IconComponent style={{ fontSize: iconSize, color: iconColor }} />}
      {noLink ? (
        <Text style={{ fontSize }}>{displayName}</Text>
      ) : (
        <Text onClick={handleClick} style={{ fontSize, cursor: 'pointer' }}>
          {displayName}
        </Text>
      )}
    </Space>
  );
}
